import { useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { getCartState } from "../../../store";
import { addProductToCart, clearCart, clearProductInCart, removeProductInCart, updateQuantityProductInCart } from "../../../store/slices/cartSlice";
import CartIcon from "../../../assets/icons/CartIcon";
import EmptyIcon from "../../../assets/icons/EmptyIcon";
import MinusIcon from "../../../assets/icons/MinusIcon";
import PlusIcon from "../../../assets/icons/PlusIcon";
import { useNavigate } from "react-router-dom";
import { getCartItemUnitPrice } from "../../../helpers/handleProduct";

export default function Cart() {
    const { cartInfo } = useAppSelector(getCartState);
    
    const navigate = useNavigate()
    const dispatch = useAppDispatch();
    const [quantity, setQuantity] = useState<number>();
    const [itemEditQuantity, setItemEditQuantity] = useState<number>();
    const [isEditQuantity, setIsEditQuantity] = useState<boolean>(false);
    const totalPrice = useMemo(() => {
        return cartInfo.reduce((accumulator, currentValue) => {
            const unitPrice = getCartItemUnitPrice(currentValue);
            return accumulator + unitPrice * currentValue.quantity;
        }, 0)
    }, [cartInfo]);
    const baseTotalPrice = useMemo(() => {
        return cartInfo.reduce((accumulator, currentValue) => {
            return accumulator + currentValue.price_sale * currentValue.quantity;
        }, 0)
    }, [cartInfo]);
    const showBaseTotal = baseTotalPrice > totalPrice;
    return (
        <div className="w-full max-md:hidden">
            <div className="sticky top-6">
                <div className="flex justify-between items-center px-3 py-4 bg-white border-b-[1px] border-[#F3F4F5] border-solid rounded-t">
                    <h3 className="font-medium text-lg text-[#182537]">Đã chọn ({cartInfo.length})</h3>
                    {cartInfo.length > 0 && (
                        <button className="text-xs text-[#FF4D4D] cursor-pointer hover:bg-[#F4F4F4] p-1" onClick={() => dispatch(clearCart())}>Xóa giỏ hàng</button>
                    )}
                </div>
                <div className="flex flex-col gap-2 bg-white py-1">
                    {!!!cartInfo.length && (
                        <div className="flex flex-col items-center gap-2 py-4">
                            <EmptyIcon />
                            <p className="text-sm text-[#747C87]">Thêm mặt hàng và đặt đơn ngay nhé</p>
                        </div>
                    )}
                    {!!cartInfo.length && cartInfo.map((item, index) => {
                        const unitPrice = getCartItemUnitPrice(item);
                        const linePrice = unitPrice * item.quantity;
                        const baseLinePrice = item.price_sale * item.quantity;
                        const showLineDiscount = unitPrice < item.price_sale;
                        return (
                            <div key={index} className="flex flex-col gap-2 px-4 py-2 w-full">
                                <h1 className="text-sm font-medium">{item.name}</h1>
                                <div className="flex justify-between gap-2 items-end max-lg:flex-col max-lg:items-start">
                                    <div className="flex items-center gap-1">
                                        <button onClick={() => {
                                            dispatch(removeProductInCart(item.id));
                                            setIsEditQuantity(prev => prev = false);
                                        }}>
                                            <MinusIcon />
                                        </button>
                                        {itemEditQuantity === item.id && isEditQuantity ?
                                            <input type="text"
                                                value={quantity}
                                                onBlur={() => {
                                                    if (!quantity) {
                                                        dispatch(clearProductInCart({ id: item.id }));
                                                        setIsEditQuantity(prev => prev = false)
                                                        return;
                                                    }
                                                    dispatch(updateQuantityProductInCart({ id: item.id, quantity: quantity }))
                                                }}
                                                onChange={(ev) => {
                                                    setQuantity(prev => prev = +ev.target.value)
                                                }}
                                                onKeyDown={(ev) => {
                                                    if (!/[0-9]/.test(ev.key)
                                                        && ev.key !== 'Backspace'
                                                        && ev.key !== 'ArrowLeft'
                                                        && ev.key !== 'ArrowRight'
                                                        && ev.key !== 'Delete') {
                                                        ev.preventDefault();
                                                    }
                                                }}
                                                className="outline-none w-14 flex-1 text-center border-b-2 border-b-[#F4F1F1] border-solid" />
                                            :
                                            <input
                                                type="text"
                                                value={item.quantity}
                                                onClick={() => {
                                                    setItemEditQuantity(prev => prev = item.id);
                                                    setQuantity(prev => prev = item.quantity);
                                                    setIsEditQuantity(prev => prev = true)
                                                }}
                                                className="outline-none w-14 flex-1 text-center border-b-2 border-b-[#F4F1F1] border-solid cursor-pointer" />}

                                        <button onClick={() => {
                                            dispatch(addProductToCart(item));
                                            setIsEditQuantity(prev => prev = false)
                                        }}>
                                            <PlusIcon />
                                        </button>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <p className="text-sm text-[#182537]">{linePrice.toLocaleString("vi-VN")}</p>
                                        {showLineDiscount && (
                                            <p className="text-xs text-[#7A8086] line-through">{baseLinePrice.toLocaleString("vi-VN")}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="flex flex-col gap-2 py-3 w-full rounded-b px-4 bg-white border-t-[1px] border-[#F3F4F5] border-solid max-md:shadow-sm max-md:shadow-gray-200">
                    <div className="flex justify-between items-center">
                        <h1 className="text-sm font-medium text-[#182537]">Tổng cộng</h1>
                        <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-[#182537]">{totalPrice.toLocaleString("vi-VN")}</p>
                            {showBaseTotal && (
                                <p className="text-xs text-[#7A8086] line-through">{baseTotalPrice.toLocaleString("vi-VN")}</p>
                            )}
                        </div>
                    </div>
                    {cartInfo.length > 0 ?
                        <button
                            className="flex justify-center items-center gap-2 bg-[#0088FF] w-full rounded py-2"
                            onClick={() => navigate('/checkout')}
                        >
                            <CartIcon color="#FFFFFF" />
                            <p className="text-xl font-medium text-white">Đặt đơn</p>
                        </button> :
                        <button disabled className="flex justify-center items-center gap-2 bg-[#E0E0E0] w-full rounded py-2">
                            <CartIcon color="#747C87" />
                            <p className="text-xl font-medium text-[#747C87]">Đặt đơn</p>
                        </button>
                    }
                </div>
            </div >
        </div>
    );
}